/**
 * Phase 11 Beat 3 — Side-by-side Change Request Review modal.
 * Shows Previous vs New for each field + Approve / Reject / Request Clarification buttons.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { X, CheckCircle, XCircle, ChatCentered, ArrowRight } from "@phosphor-icons/react";

export default function ChangeRequestReviewModal({ crId, onClose, onDone }) {
  const [cr, setCr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(null);   // "approve" | "reject" | "clarify"
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/change-requests/${crId}`);
      setCr(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
  }, [crId]);
  useEffect(() => { load(); }, [load]);

  const act = async (kind) => {
    if ((kind === "reject" || kind === "clarify") && !reason.trim()) {
      toast.error(kind === "reject" ? "Rejection reason is required." : "Clarification note is required.");
      return;
    }
    setBusy(true);
    try {
      const url = kind === "approve" ? `/change-requests/${crId}/approve`
                : kind === "reject" ? `/change-requests/${crId}/reject`
                : `/change-requests/${crId}/request-clarification`;
      const r = await api.post(url, { reason: reason.trim() });
      if (r.data.ok) {
        toast.success(kind === "approve" ? "Approved — candidate profile updated"
                    : kind === "reject" ? "Rejected — candidate has been notified"
                    : "Clarification requested — candidate has been notified");
        onDone && onDone();
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };

  const fmt = (v) => {
    if (v == null || v === "") return <span className="text-slate-400 italic">empty</span>;
    if (Array.isArray(v)) return v.join(", ");
    if (typeof v === "boolean") return v ? "yes" : "no";
    if (typeof v === "object") return JSON.stringify(v);
    return String(v);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4" data-testid="cr-review-modal">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500">Change Request Review</div>
            <h3 className="text-lg font-semibold text-slate-900">
              {cr?.category ? cr.category.charAt(0).toUpperCase() + cr.category.slice(1) : "…"}
              {cr?.candidate?.full_name && (
                <span className="text-slate-500 font-normal"> · {cr.candidate.full_name}</span>
              )}
            </h3>
          </div>
          <button onClick={onClose} data-testid="cr-review-close"
                  className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-500">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {!cr && <div className="text-sm text-slate-400">Loading…</div>}
          {cr && (
            <>
              <div className="text-xs text-slate-500 flex items-center gap-3 flex-wrap">
                <span>Status: <span className={`font-medium capitalize ${cr.status === "pending_review" ? "text-amber-700"
                  : cr.status === "approved" ? "text-emerald-700"
                  : cr.status === "rejected" ? "text-red-700" : "text-slate-800"}`}>{cr.status.replace("_", " ")}</span></span>
                <span>Submitted: {(cr.created_at || "").slice(0, 10)}</span>
                {cr.candidate?.email && <span>· {cr.candidate.email}</span>}
              </div>

              {cr.candidate_notes && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-700" data-testid="cr-candidate-notes">
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Candidate note</div>
                  {cr.candidate_notes}
                </div>
              )}

              <div className="space-y-2">
                <div className="text-xs uppercase tracking-widest text-slate-500">Proposed changes</div>
                {Object.entries(cr.fields || {}).map(([path, ch]) => (
                  <div key={path} className="border border-slate-200 rounded-xl p-3 grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-2 items-center"
                       data-testid={`cr-field-diff-${path.replace(/\./g, "-")}`}>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-400">{path}</div>
                      <div className="text-xs text-slate-500 mt-0.5">Previous:</div>
                      <div className="text-sm text-slate-800 line-through decoration-red-400 decoration-1">{fmt(ch.previous)}</div>
                    </div>
                    <ArrowRight size={16} className="text-slate-400 hidden md:block" />
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-emerald-500">New</div>
                      <div className="text-sm text-emerald-800 font-medium mt-0.5">{fmt(ch.new)}</div>
                    </div>
                  </div>
                ))}
              </div>

              {cr.status === "pending_review" && (
                <>
                  {mode && (
                    <div className="border border-slate-200 rounded-xl p-3 space-y-2">
                      <div className="text-xs font-medium text-slate-700">
                        {mode === "approve" ? "Optional approval note (visible to the candidate):"
                          : mode === "reject" ? "Rejection reason (required — shown to the candidate):"
                          : "What additional information do you need? (required — shown to the candidate):"}
                      </div>
                      <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
                                data-testid="cr-review-reason"
                                placeholder={mode === "approve" ? "e.g. Verified with client team on Feb 20"
                                  : mode === "reject" ? "e.g. Please attach the new resume file before resubmitting."
                                  : "e.g. Please clarify the exact effective date for your visa change."}
                                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-sky-500 resize-none" />
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => { setMode(null); setReason(""); }} disabled={busy}
                                className="text-sm border border-slate-200 rounded-lg px-3 py-2 hover:bg-slate-50">Cancel</button>
                        <button onClick={() => act(mode)} disabled={busy}
                                data-testid={`cr-confirm-${mode}`}
                                className={`text-sm text-white rounded-lg px-4 py-2 font-medium inline-flex items-center gap-1.5 disabled:opacity-40
                                            ${mode === "approve" ? "bg-emerald-600 hover:bg-emerald-700"
                                              : mode === "reject" ? "bg-red-600 hover:bg-red-700"
                                              : "bg-amber-600 hover:bg-amber-700"}`}>
                          Confirm {mode === "approve" ? "Approval" : mode === "reject" ? "Rejection" : "Clarification"}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}

              {cr.status !== "pending_review" && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600 space-y-1" data-testid="cr-decision-info">
                  {cr.decision_reason && <div><span className="text-slate-400">Reason:</span> {cr.decision_reason}</div>}
                  {cr.clarification_notes && <div><span className="text-slate-400">Clarification:</span> {cr.clarification_notes}</div>}
                  {cr.reviewer && <div><span className="text-slate-400">Reviewed by:</span> {cr.reviewer.name || cr.reviewer.email} on {(cr.decision_at || "").slice(0, 10)}</div>}
                </div>
              )}
            </>
          )}
        </div>

        {cr && cr.status === "pending_review" && !mode && (
          <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-slate-100 shrink-0 bg-slate-50">
            <button onClick={() => { setMode("clarify"); setReason(""); }} disabled={busy}
                    data-testid="cr-btn-clarify"
                    className="text-sm border border-amber-300 text-amber-800 hover:bg-amber-50 rounded-lg px-3 py-2 inline-flex items-center gap-1.5 font-medium">
              <ChatCentered size={14} /> Request Clarification
            </button>
            <button onClick={() => { setMode("reject"); setReason(""); }} disabled={busy}
                    data-testid="cr-btn-reject"
                    className="text-sm border border-red-300 text-red-800 hover:bg-red-50 rounded-lg px-3 py-2 inline-flex items-center gap-1.5 font-medium">
              <XCircle size={14} /> Reject
            </button>
            <button onClick={() => { setMode("approve"); setReason(""); }} disabled={busy}
                    data-testid="cr-btn-approve"
                    className="text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-3 py-2 inline-flex items-center gap-1.5 font-medium">
              <CheckCircle size={14} /> Approve
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
